import React, { useState } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

export const DEFAULT_VALIDATION_INFO_TRUNCATE_LENGTH = 100;
const TOOLTIP_MAX_LENGTH = 500;
const EMPTY_TEXT = 'No instructions provided';

interface IValidationInfoCellRendererProps extends ICellRendererParams {
    value: string | null | undefined;
    truncateLength?: number;
}

const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
        return text;
    }

    return `${text.slice(0, maxLength).trimEnd()}...`;
};

// Read-only display of the client-computed, display-only Instructions text (see
// getValidationInfoDisplayText in validationInfoDisplay.ts). This is rendered from a
// separate `validationInfoDisplay` row field so it is never confused with the real,
// persisted dbo.WorksheetColumns.ValidationInfo value, which round-trips through
// save/load untouched. The cell is styled to look disabled (matching the placeholder-text
// color, non-italic). Since the message can be multi-line/lengthy, the cell can still be
// clicked (or activated via keyboard) to view the entire text in a read-only dialog; the
// popup itself is not editable/clickable (just a read-only text area).
const ValidationInfoCellRenderer: React.FC<IValidationInfoCellRendererProps> = (params) => {
    const truncateLength = params.truncateLength ?? DEFAULT_VALIDATION_INFO_TRUNCATE_LENGTH;
    const [open, setOpen] = useState(false);

    const currentValue: string = params.value ?? '';
    const hasOverride = currentValue.trim().length > 0;

    const displayText = hasOverride
        ? truncateText(currentValue, truncateLength)
        : EMPTY_TEXT;

    const tooltipText = truncateText(currentValue, TOOLTIP_MAX_LENGTH);

    const handleOpen = () => {
        if (!hasOverride) return;
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            handleOpen();
        }
    };

    const content = (
        <Box
            role={hasOverride ? 'button' : undefined}
            tabIndex={hasOverride ? 0 : undefined}
            aria-label={hasOverride ? 'View full instructions' : undefined}
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                cursor: hasOverride ? 'pointer' : 'default',
                '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                },
            }}
        >
            <Typography
                noWrap
                variant="body2"
                sx={{
                    color: 'grey.500',
                    fontStyle: hasOverride ? 'normal' : 'italic',
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    pointerEvents: 'none',
                }}
            >
                {displayText}
            </Typography>
        </Box>
    );

    return (
        <>
            {hasOverride ? (
                <Tooltip
                    title={
                        <>
                            {tooltipText.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && <br />}
                                    {line}
                                </React.Fragment>
                            ))}
                        </>
                    }
                    placement="top"
                    arrow
                >
                    {content}
                </Tooltip>
            ) : content}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Instructions</DialogTitle>
                <DialogContent>
                    <TextField
                        multiline
                        fullWidth
                        minRows={8}
                        maxRows={20}
                        variant="outlined"
                        value={currentValue}
                        InputProps={{ readOnly: true }}
                        sx={{
                            marginTop: 1,
                            '& .MuiInputBase-input': {
                                color: 'grey.500',
                                fontStyle: 'normal',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ValidationInfoCellRenderer;



